/**
 * AmplitudeEngagementService wraps the Amplitude Guides & Surveys SDK and provides
 * integration with Segment analytics. This service initializes Amplitude Engagement
 * SDK and forwards Segment track events to Amplitude for guide/survey triggering.
 *
 * Based on Amplitude documentation:
 * https://amplitude.com/docs/guides-and-surveys/sdk
 */

const ENGAGEMENT_STUB = {
  init: () => {},
  boot: () => Promise.resolve(),
  shutdown: () => {},
  forwardEvent: () => {},
  _setUserProperties: () => {},
  setSessionProperty: () => {},
  setRouter: () => {},
  gs: {
    show: () => {},
    closeAll: () => {},
    reset: () => {},
    list: () => [],
  },
};

/**
 * Service to manage Amplitude Guides & Surveys integration with Segment
 */
export default class AmplitudeEngagementService {
  constructor(apiKey, segmentAnalytics) {
    this.apiKey = apiKey;
    this.segmentAnalytics = segmentAnalytics;
    this.engagement = ENGAGEMENT_STUB;
    this.isInitialized = false;
    this.isBooted = false;
    this.pendingBootUser = null;
  }

  /**
   * Initialize the Amplitude Engagement SDK
   * @param {Object} options - Initialization options
   * @param {string} options.serverZone - 'US' or 'EU'
   * @param {string} options.logLevel - Log level (LogLevel.Warn, LogLevel.Error, etc.)
   * @param {string} options.locale - Locale for localization
   * @param {string} options.nonce - CSP nonce value
   */
  init(options = {}) {
    if (this.isInitialized) return;

    if (!this.apiKey || typeof window === 'undefined') return;

    try {
      // Wait for window.engagement to be available (loaded via script tag)
      if (window.engagement && window.engagement.init) {
        window.engagement.init(this.apiKey, {
          serverZone: options.serverZone || 'US',
          logLevel: options.logLevel,
          locale: options.locale,
          nonce: options.nonce,
        });
        this.engagement = window.engagement;
        this.isInitialized = true;

        // Boot with pending user if available
        if (this.pendingBootUser) {
          this.boot(this.pendingBootUser);
        }
      } else {
        // If SDK not loaded yet, wait for it
        const checkInterval = setInterval(() => {
          if (window.engagement && window.engagement.init) {
            clearInterval(checkInterval);
            this.init(options);
          }
        }, 100);

        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!this.isInitialized) {
            console.warn('Amplitude Engagement SDK failed to load');
          }
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to initialize Amplitude Engagement SDK:', error);
    }
  }

  /**
   * Boot the Amplitude Engagement SDK with user information
   * This must be called before guides/surveys can be displayed
   * @param {Object} userInfo - User information
   * @param {string|Function} userInfo.user_id - User ID or function returning user ID
   * @param {string|Function} userInfo.device_id - Device ID or function returning device ID
   * @param {Object|Function} userInfo.user_properties - User properties or function returning properties
   */
  async boot(userInfo = {}) {
    if (!this.isInitialized || !this.engagement || this.engagement === ENGAGEMENT_STUB) {
      this.pendingBootUser = userInfo;
      return;
    }

    if (this.isBooted) {
      return;
    }

    try {
      // Set up integration to forward Segment track events to Amplitude
      const integrations = [
        {
          track: (event) => {
            // Forward Amplitude Engagement events to Segment
            if (this.segmentAnalytics && this.segmentAnalytics.track) {
              this.segmentAnalytics.track(event.event_type, event.event_properties);
            }
          },
        },
      ];

      // Boot with user information and Segment integration
      await this.engagement.boot({
        user: userInfo.user_id
          ? {
              user_id: userInfo.user_id,
              device_id: userInfo.device_id,
              user_properties: userInfo.user_properties || {},
            }
          : userInfo,
        integrations,
      });

      this.isBooted = true;
      this.pendingBootUser = null;

      // Set up listener to forward Segment track events to Amplitude
      this._setupSegmentIntegration();
    } catch (error) {
      console.error('Failed to boot Amplitude Engagement SDK:', error);
    }
  }

  /**
   * Set up integration to forward Segment track events to Amplitude Engagement
   * This enables guides/surveys triggered by "On event tracked" to work
   * Note: This is handled via SegmentService.track() forwarding events directly
   */
  _setupSegmentIntegration() {
    // Integration is handled via SegmentService.track() forwarding events
    // This method is kept for potential future use with Segment's event listeners
  }

  /**
   * Forward a Segment track event to Amplitude Engagement
   * This is called manually when SegmentService.track is invoked
   * @param {string} eventName - Event name
   * @param {Object} properties - Event properties
   */
  forwardEvent(eventName, properties = {}) {
    if (this.engagement && this.engagement.forwardEvent && this.isBooted) {
      this.engagement.forwardEvent({
        event_type: eventName,
        event_properties: properties,
      });
    }
  }

  /**
   * Set user properties for the current session
   * @param {Object} userProperties - User properties object
   */
  setUserProperties(userProperties) {
    if (this.engagement && this.engagement._setUserProperties && this.isBooted) {
      this.engagement._setUserProperties(userProperties);
    }
  }

  /**
   * Set session properties for targeting
   * @param {string} key - Session property key
   * @param {any} value - Session property value
   */
  setSessionProperty(key, value) {
    if (this.engagement && this.engagement.setSessionProperty && this.isBooted) {
      this.engagement.setSessionProperty(key, value);
    }
  }

  /**
   * Configure router for SPA navigation
   * @param {Function} routerFn - Router function that handles URL changes
   */
  setRouter(routerFn) {
    if (this.engagement && this.engagement.setRouter && this.isInitialized) {
      this.engagement.setRouter(routerFn);
    }
  }

  /**
   * Shutdown the Amplitude Engagement SDK
   */
  shutdown() {
    if (this.engagement && this.engagement.shutdown && this.isBooted) {
      this.engagement.shutdown();
      this.isBooted = false;
    }
  }

  /**
   * Show a specific guide or survey
   * @param {string} key - Guide or survey key
   * @param {number} stepIndex - Optional step index (zero-based)
   */
  show(key, stepIndex) {
    if (this.engagement && this.engagement.gs && this.engagement.gs.show && this.isBooted) {
      this.engagement.gs.show(key, stepIndex);
    }
  }

  /**
   * Close all active guides and surveys
   */
  closeAll() {
    if (this.engagement && this.engagement.gs && this.engagement.gs.closeAll && this.isBooted) {
      this.engagement.gs.closeAll();
    }
  }

  /**
   * Reset a guide or survey to a specific step
   * @param {string} key - Guide or survey key
   * @param {number} stepIndex - Step index (zero-based)
   */
  reset(key, stepIndex) {
    if (this.engagement && this.engagement.gs && this.engagement.gs.reset && this.isBooted) {
      this.engagement.gs.reset(key, stepIndex);
    }
  }

  /**
   * Get list of all live guides and surveys
   * @returns {Array} List of guides and surveys
   */
  list() {
    if (this.engagement && this.engagement.gs && this.engagement.gs.list && this.isBooted) {
      return this.engagement.gs.list();
    }
    return [];
  }
}
